import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Upload, Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { fetchSimCardsPaged, createSimCard, updateSimCard, deleteSimCard, importSimCards, fetchDeviceStock, updateDeviceStock, SimCard, DeviceStock } from "@/lib/reseller-api";

const statusColors = {
  active: "bg-green-500",
  inactive: "bg-gray-500",
  suspended: "bg-red-500",
};

export default function SimCards() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<SimCard | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") ?? "");
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: pagedResult, isLoading } = useQuery({
    queryKey: ["sim-cards", "paged", page, search],
    queryFn: () => fetchSimCardsPaged({ page, limit: PAGE_SIZE, search }),
  });
  const simCards = pagedResult?.data ?? [];
  const total = pagedResult?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const { data: deviceStock = [] } = useQuery({
    queryKey: ["device-stock"],
    queryFn: fetchDeviceStock,
  });

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("none");
  const [deviceOpen, setDeviceOpen] = useState(false);

  useEffect(() => {
    if (editing) {
      const linked = deviceStock.find((d) => d.sim_card_id === editing.id);
      setSelectedDeviceId(linked?.id ?? "none");
    } else {
      setSelectedDeviceId("none");
    }
  }, [editing?.id, deviceStock]);

  const pendingDeviceIdRef = useRef<string>("none");

  const createMutation = useMutation({
    mutationFn: createSimCard,
    onSuccess: async (newSimCard) => {
      if (pendingDeviceIdRef.current !== "none") {
        try {
          await updateDeviceStock(pendingDeviceIdRef.current, { sim_card_id: newSimCard.id });
          queryClient.invalidateQueries({ queryKey: ["device-stock"] });
        } catch { /* sim card still created */ }
        pendingDeviceIdRef.current = "none";
      }
      queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      setIsOpen(false);
      toast({ title: t("reseller.simCard.created") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SimCard> }) => updateSimCard(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      setIsOpen(false);
      setEditing(null);
      toast({ title: t("reseller.simCard.updated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSimCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      toast({ title: t("reseller.simCard.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [colMap, setColMap] = useState<{ icc: string; msisdn: string; imei: string; apn: string; carrier: string }>({ icc: "", msisdn: "", imei: "", apn: "", carrier: "" });
  const [overrides, setOverrides] = useState<{ apn: string; carrier: string }>({ apn: "", carrier: "" });

  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number; step: string } | null>(null);

  useEffect(() => {
    if (!importing) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [importing]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    if (!bulkDeleting) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [bulkDeleting]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === simCards.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(simCards.map((s) => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!confirm(t("reseller.simCard.bulkDeleteConfirm", { count: ids.length }))) return;
    setBulkDeleting(true);
    setBulkDeleteProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        await deleteSimCard(ids[i]);
        setBulkDeleteProgress({ done: i + 1, total: ids.length });
      }
      queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      setSelectedIds(new Set());
      toast({ title: t("reseller.simCard.bulkDeleted", { count: ids.length }) });
    } catch (err) {
      toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
    } finally {
      setBulkDeleting(false);
      setBulkDeleteProgress(null);
    }
  };

  const NONE = "__none__";

  const detectColumn = (headers: string[], ...hints: string[]) =>
    headers.find((h) => hints.some((hint) => h.toLowerCase().includes(hint))) ?? NONE;

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = (ev.target?.result as string).replace(/^\uFEFF/, "");
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) return;
      const delimiter = lines[0].includes(";") ? ";" : ",";
      const headers = lines[0].split(delimiter).map((h) => h.trim());
      const rows = lines.slice(1).map((line) => line.split(delimiter).map((c) => c.trim()));
      setCsvHeaders(headers);
      setCsvRows(rows);
      setColMap({
        icc: detectColumn(headers, "icc", "iccid"),
        msisdn: detectColumn(headers, "msisdn", "phone"),
        imei: detectColumn(headers, "imei"),
        apn: detectColumn(headers, "apn"),
        carrier: detectColumn(headers, "carrier", "provider", "operadora"),
      });
      setOverrides({ apn: "", carrier: "" });
      setPreviewOpen(true);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const colVal = (col: string, row: string[]) =>
    col && col !== NONE ? (row[csvHeaders.indexOf(col)] ?? "") : "";

  const BATCH_SIZE = 200;

  const handleConfirmImport = async () => {
    const rows = csvRows
      .map((row) => ({
        icc: colVal(colMap.icc, row),
        msisdn: colVal(colMap.msisdn, row),
        imei: colVal(colMap.imei, row),
        apn: overrides.apn || colVal(colMap.apn, row),
        carrier: overrides.carrier || colVal(colMap.carrier, row),
      }))
      .filter((r) => r.icc);

    setImporting(true);
    const totals = { sim_cards: 0, devices_created: 0, devices_linked: 0 };

    try {
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        setImportProgress({ done: i, total: rows.length, step: `Importing rows ${i + 1}–${Math.min(i + BATCH_SIZE, rows.length)}…` });
        const result = await importSimCards(batch);
        totals.sim_cards += result.sim_cards;
        totals.devices_created += result.devices_created;
        totals.devices_linked += result.devices_linked;
      }
      queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      setPreviewOpen(false);
      toast({
        title: t("reseller.simCard.importComplete"),
        description: t("reseller.simCard.importCompleteDesc", { simCards: totals.sim_cards, devicesCreated: totals.devices_created, devicesLinked: totals.devices_linked }),
      });
    } catch (err) {
      toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      iccid: formData.get("iccid") as string,
      phone_number: formData.get("phone_number") as string,
      carrier: formData.get("carrier") as string,
      apn: formData.get("apn") as string || null,
      data_plan: formData.get("data_plan") as string,
      status: formData.get("status") as SimCard["status"],
      activation_date: formData.get("activation_date") as string || null,
      expiry_date: formData.get("expiry_date") as string || null,
    };

    if (editing) {
      const prevDevice = deviceStock.find((d) => d.sim_card_id === editing.id);
      const prevDeviceId = prevDevice?.id ?? "none";
      if (prevDeviceId !== selectedDeviceId) {
        if (prevDevice) await updateDeviceStock(prevDevice.id, { sim_card_id: null });
        if (selectedDeviceId !== "none") await updateDeviceStock(selectedDeviceId, { sim_card_id: editing.id });
        queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      }
      updateMutation.mutate({ id: editing.id, data });
    } else {
      pendingDeviceIdRef.current = selectedDeviceId;
      createMutation.mutate(data);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {(importing || bulkDeleting) && (
          <div className="bg-destructive/10 border border-destructive rounded-lg px-4 py-3 flex items-center gap-3">
            <span className="text-lg font-semibold text-destructive animate-pulse">{t("common.doNotLeave")}</span>
            {importProgress && <span className="text-sm text-destructive">{importProgress.done} / {importProgress.total}</span>}
            {bulkDeleteProgress && <span className="text-sm text-destructive">{bulkDeleteProgress.done} / {bulkDeleteProgress.total}</span>}
          </div>
        )}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">
            {!isLoading && <span className="text-muted-foreground text-xl font-normal ml-2">{t("reseller.simCard.count", { count: total })}</span>}
          </h1>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder={t("reseller.simCard.searchPlaceholder")} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
              </div>
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                <Upload className="h-4 w-4 mr-2" />
                {importing ? t("reseller.simCard.importing") : t("reseller.simCard.import")}
              </Button>
              {(selectedIds.size > 0 || bulkDeleting) && (
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {bulkDeleteProgress
                    ? `${bulkDeleteProgress.done} / ${bulkDeleteProgress.total}`
                    : `${t("common.delete")} (${selectedIds.size})`}
                </Button>
              )}
              <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setEditing(null); }}>
                <DialogTrigger asChild>
                  <Button><Plus className="h-4 w-4 mr-2" /> {t("reseller.simCard.add")}</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editing ? t("reseller.simCard.edit") : t("reseller.simCard.add")}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="iccid">{t("reseller.simCard.iccid")} *</Label>
                      <Input id="iccid" name="iccid" required defaultValue={editing?.iccid} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone_number">{t("reseller.simCard.phoneNumber")}</Label>
                        <Input id="phone_number" name="phone_number" defaultValue={editing?.phone_number || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="carrier">{t("reseller.simCard.carrier")}</Label>
                        <Input id="carrier" name="carrier" defaultValue={editing?.carrier || ""} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apn">APN</Label>
                      <Input id="apn" name="apn" defaultValue={editing?.apn || ""} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="data_plan">{t("reseller.simCard.dataPlan")}</Label>
                        <Input id="data_plan" name="data_plan" defaultValue={editing?.data_plan || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t("reseller.simCard.device")}</Label>
                        <Popover open={deviceOpen} onOpenChange={setDeviceOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" aria-expanded={deviceOpen} className="w-full justify-between font-normal">
                              <span className="truncate">
                                {selectedDeviceId === "none"
                                  ? t("reseller.simCard.noDevice")
                                  : (() => { const d = deviceStock.find((x) => x.id === selectedDeviceId); return d ? `${d.model}${d.imei ? ` — ${d.imei}` : ""}` : t("reseller.simCard.noDevice"); })()}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command>
                              <CommandInput placeholder={t("reseller.simCard.searchDevice")} />
                              <CommandList>
                                <CommandEmpty>{t("reseller.simCard.noDeviceFound")}</CommandEmpty>
                                <CommandGroup>
                                  <CommandItem value="none" onSelect={() => { setSelectedDeviceId("none"); setDeviceOpen(false); }}>
                                    <Check className={cn("mr-2 h-4 w-4", selectedDeviceId === "none" ? "opacity-100" : "opacity-0")} />
                                    {t("reseller.simCard.noDevice")}
                                  </CommandItem>
                                  {deviceStock.map((d) => {
                                    const label = `${d.model}${d.imei ? ` — ${d.imei}` : ""}${d.serial_number ? ` (${d.serial_number})` : ""}`;
                                    return (
                                      <CommandItem key={d.id} value={label} onSelect={() => { setSelectedDeviceId(d.id); setDeviceOpen(false); }}>
                                        <Check className={cn("mr-2 h-4 w-4", selectedDeviceId === d.id ? "opacity-100" : "opacity-0")} />
                                        {label}
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="status">{t("reseller.simCard.status")}</Label>
                        <Select name="status" defaultValue={editing?.status || "active"}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">{t("reseller.simCard.statuses.active")}</SelectItem>
                            <SelectItem value="inactive">{t("reseller.simCard.statuses.inactive")}</SelectItem>
                            <SelectItem value="suspended">{t("reseller.simCard.statuses.suspended")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="activation_date">{t("reseller.simCard.activationDate")}</Label>
                        <Input id="activation_date" name="activation_date" type="date" defaultValue={editing?.activation_date || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="expiry_date">{t("reseller.simCard.expiryDate")}</Label>
                        <Input id="expiry_date" name="expiry_date" type="date" defaultValue={editing?.expiry_date || ""} />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editing ? t("common.update") : t("common.create")}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : total === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.simCard.noSimCards")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={simCards.length > 0 && selectedIds.size === simCards.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>{t("reseller.simCard.iccid")}</TableHead>
                    <TableHead>{t("reseller.simCard.phoneNumber")}</TableHead>
                    <TableHead>{t("reseller.simCard.carrier")}</TableHead>
                    <TableHead>APN</TableHead>
                    <TableHead>{t("reseller.simCard.device")}</TableHead>
                    <TableHead>{t("reseller.simCard.status")}</TableHead>
                    <TableHead className="w-[100px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simCards.map((sim) => (
                    <TableRow key={sim.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(sim.id)}
                          onCheckedChange={() => toggleSelect(sim.id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{sim.iccid}</TableCell>
                      <TableCell>{sim.phone_number || "-"}</TableCell>
                      <TableCell>{sim.carrier || "-"}</TableCell>
                      <TableCell>{sim.apn || "-"}</TableCell>
                      <TableCell>{(() => { const d = deviceStock.find((ds) => ds.sim_card_id === sim.id); return d ? `${d.model}${d.imei ? ` — ${d.imei}` : ""}` : "-"; })()}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[sim.status]}>{t(`reseller.simCard.statuses.${sim.status}`)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => { setEditing(sim); setIsOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate(sim.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-1 pt-4">
                <span className="text-sm text-muted-foreground">
                  {t("common.showingRange", { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>{t("common.previous")}</Button>
                  <span className="text-sm text-muted-foreground">{t("common.pageOf", { page, total: totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>{t("common.next")}</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={previewOpen} onOpenChange={(open) => { if (!importing) setPreviewOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t("reseller.simCard.importTitle", { count: csvRows.length })}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-3 gap-3">
              {(["icc", "msisdn", "imei"] as const).map((field) => {
                const labels = { icc: "ICCID *", msisdn: t("reseller.simCard.phoneNumber"), imei: "IMEI" };
                return (
                  <div key={field} className="space-y-1">
                    <Label>{labels[field]}</Label>
                    <Select value={colMap[field] || NONE} onValueChange={(v) => setColMap((m) => ({ ...m, [field]: v === NONE ? "" : v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.simCard.importSkip")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>{t("reseller.simCard.importSkip")}</SelectItem>
                        {csvHeaders.map((h) => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(["apn", "carrier"] as const).map((field) => {
                const labels = { apn: "APN", carrier: t("reseller.simCard.carrier") };
                return (
                  <div key={field} className="space-y-1">
                    <Label>{labels[field]}</Label>
                    <div className="flex gap-2">
                      <Select value={overrides[field] ? "__override__" : (colMap[field] || NONE)} onValueChange={(v) => {
                        if (v === "__override__") return;
                        setColMap((m) => ({ ...m, [field]: v === NONE ? "" : v }));
                        setOverrides((o) => ({ ...o, [field]: "" }));
                      }}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={t("reseller.simCard.importSkip")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>{t("reseller.simCard.importSkip")}</SelectItem>
                          {csvHeaders.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="self-center text-xs text-muted-foreground">or</span>
                      <Input
                        className="flex-1"
                        placeholder={`Same ${labels[field]} for all`}
                        value={overrides[field]}
                        onChange={(e) => {
                          setOverrides((o) => ({ ...o, [field]: e.target.value }));
                          if (e.target.value) setColMap((m) => ({ ...m, [field]: "" }));
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-md border overflow-auto max-h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    {csvHeaders.map((h) => {
                        const active = [colMap.icc, colMap.msisdn, colMap.imei, colMap.apn, colMap.carrier].filter((v) => v && v !== NONE).includes(h);
                        return (
                          <TableHead key={h} className={active ? "bg-muted font-semibold" : ""}>
                            {h}
                            {colMap.icc === h && <span className="ml-1 text-xs text-muted-foreground">(ICCID)</span>}
                            {colMap.msisdn === h && <span className="ml-1 text-xs text-muted-foreground">({t("reseller.simCard.phoneNumber")})</span>}
                            {colMap.imei === h && <span className="ml-1 text-xs text-muted-foreground">(IMEI)</span>}
                            {colMap.apn === h && <span className="ml-1 text-xs text-muted-foreground">(APN)</span>}
                            {colMap.carrier === h && <span className="ml-1 text-xs text-muted-foreground">({t("reseller.simCard.carrier")})</span>}
                          </TableHead>
                        );
                      })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvRows.slice(0, 10).map((row, i) => (
                    <TableRow key={i}>
                      {csvHeaders.map((h, j) => {
                        const active = [colMap.icc, colMap.msisdn, colMap.imei, colMap.apn, colMap.carrier].filter((v) => v && v !== NONE).includes(h);
                        return (
                        <TableCell key={j} className={active ? "bg-muted/40 font-mono text-xs" : "text-xs text-muted-foreground"}>
                          {row[j] ?? ""}
                        </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {csvRows.length > 10 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  {t("reseller.simCard.importPreview", { shown: 10, total: csvRows.length })}
                </p>
              )}
            </div>
          </div>

          {importProgress && (
            <div className="space-y-1 pt-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{importProgress.step}</span>
                <div className="flex items-center gap-3">
                  <span>{importProgress.done} / {importProgress.total}</span>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${(importProgress.done / importProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={importing}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleConfirmImport} disabled={!colMap.icc || colMap.icc === NONE || importing}>
              {importing ? t("reseller.simCard.importing") : t("reseller.simCard.importRows", { count: colMap.icc && colMap.icc !== NONE ? csvRows.filter((r) => r[csvHeaders.indexOf(colMap.icc)]).length : 0 })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
